import React, { ReactNode, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { theme } from "../configs/theme";
import { usePwaViewportWidth } from "../hooks/useScreenWidth";
import { Row } from "../styles/Utils";
import ArrowIcon from "./svgs/icons/Arrow.icon";

interface SliderProps {
  children: ReactNode;
  relativeSizing?: boolean;
  isOverflowHidden?: boolean;
  buttonColor?: string;
}

export default function Slider({
  children,
  relativeSizing = false,
  isOverflowHidden: isCardStatic = false,
  buttonColor = theme.color.tertiary,
}: SliderProps) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const cardList = React.Children.toArray(children);
  const cardListLength = cardList.length;

  const theme = useTheme();

  const screenWidth = usePwaViewportWidth();

  const handlePrev = () => {
    // mixpanel.track("Slider Go Previous");
    setCurrentIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
  };

  const handleNext = () => {
    // mixpanel.track("Slider Go Next");
    setCurrentIndex((prevIndex) => (prevIndex < cardListLength - 1 ? prevIndex + 1 : prevIndex));
  };

  useEffect(() => {
    // Function to handle keydown events
    const handleKeyDown = (event: { key: string }) => {
      if (event.key === "ArrowLeft") {
        handlePrev();
      } else if (event.key === "ArrowRight") {
        handleNext();
      }
    };

    // Attach the event listener for keydown
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <RootContainer $overflowHidden={isCardStatic}>
      {currentIndex > 0 && (
        <BallButton onClick={handlePrev} $position="left" $buttonColor={buttonColor}>
          <ArrowIcon color={theme.color.tertiaryLight} />
        </BallButton>
      )}
      <SliderWrapper
        $currentIndex={currentIndex}
        $viewportWidth={screenWidth}
        $cardListLength={cardListLength}
        $relativeSizing={relativeSizing}
      >
        {cardList.map((child, index) => (
          <ItemWrapper key={index} $viewportWidth={screenWidth} $relativeSizing={relativeSizing}>
            {child}
          </ItemWrapper>
        ))}
      </SliderWrapper>
      {currentIndex < cardListLength - 1 && (
        <BallButton onClick={handleNext} $position="right" $buttonColor={buttonColor}>
          <ArrowIcon color={theme.color.tertiaryLight} />
        </BallButton>
      )}
    </RootContainer>
  );
}

const BallButton = styled.div<{
  $position: "right" | "left";
  $buttonColor: string;
}>`
  border-radius: 100px;
  background-color: ${(props) => props.$buttonColor ?? props.theme.color.tertiary};
  width: 4rem;
  height: 4rem;
  font-size: ${(props) => props.theme.font.size.title.medium};
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: ${(props) => (props.$position == "left" ? "5px" : null)};
  right: ${(props) => (props.$position == "right" ? "5px" : null)};
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  padding: 0.7rem;
  > svg {
    width: 100%;
    height: 100%;
    transform: ${(props) => (props.$position == "left" ? "rotateZ(180deg)" : null)};
  }
`;

const ItemWrapper = styled.div<{
  $viewportWidth: number;
  $relativeSizing: boolean;
}>`
  width: ${({ $relativeSizing, $viewportWidth }) => ($relativeSizing ? "100%" : $viewportWidth + "px")};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SliderWrapper = styled(Row)<{
  $currentIndex: number;
  $viewportWidth: number;
  $cardListLength: number;
  $relativeSizing: boolean;
}>`
  width: ${({ $relativeSizing, $viewportWidth }) => ($relativeSizing ? "100%" : $viewportWidth + "px")};
  transform: ${({ $relativeSizing, $viewportWidth, $currentIndex }) =>
    $relativeSizing
      ? `translateX(-${$currentIndex * 100 + "%"})`
      : `translateX(-${$currentIndex * $viewportWidth + "px"})`};
  transition: transform 0.3s ease-in-out;
`;

const RootContainer = styled.div<{ $overflowHidden: boolean }>`
  position: relative;
  overflow: ${(props) => (props.$overflowHidden ? "hidden" : "visible")};
`;
